import { useEffect, useState, useCallback } from "react";
import { getFileBlobUrl } from "@/lib/blobCache";
import { downloadFile } from "@/lib/files";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Download, Loader2 } from "lucide-react";

// images: array of image file objects; index: active index (null = closed)
export function ImageLightbox({ images, index, onIndexChange, onClose }) {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const open = index != null && index >= 0 && index < images.length;
  const current = open ? images[index] : null;

  useEffect(() => {
    if (!open) { setSrc(null); return; }
    let active = true;
    setSrc(null);
    setLoading(true);
    getFileBlobUrl(current.id)
      .then(({ url }) => { if (active) setSrc(url); })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, current?.id]);

  const go = useCallback((delta) => {
    if (!open) return;
    const next = (index + delta + images.length) % images.length;
    onIndexChange(next);
  }, [open, index, images.length, onIndexChange]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, go]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl rounded-sm p-0 overflow-hidden bg-gray-900 border-gray-800" data-testid="image-lightbox">
        <DialogTitle className="sr-only">{current?.original_filename || "Image preview"}</DialogTitle>
        <div className="relative flex items-center justify-center bg-gray-900 min-h-[60vh] max-h-[78vh]">
          {loading && <Loader2 className="w-8 h-8 text-white/70 animate-spin" />}
          {src && (
            <img src={src} alt={current?.original_filename}
              className="max-h-[78vh] max-w-full object-contain" data-testid="lightbox-image" />
          )}

          {images.length > 1 && (
            <>
              <button onClick={() => go(-1)} data-testid="lightbox-prev"
                className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-colors">
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button onClick={() => go(1)} data-testid="lightbox-next"
                className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-colors">
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-4 py-3 bg-gray-900 border-t border-gray-800">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white truncate" data-testid="lightbox-filename">{current?.original_filename}</p>
            {images.length > 1 && <p className="text-xs text-gray-400">{index + 1} of {images.length}</p>}
          </div>
          <button onClick={() => current && downloadFile(current.id, current.original_filename)} data-testid="lightbox-download"
            className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded-sm font-semibold text-sm transition-colors shrink-0">
            <Download className="w-4 h-4" /> Download
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
